import { Plus, PowerOff, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  getEspecialidadesDiscussaoAdminControllerListarQueryKey,
  useEspecialidadesDiscussaoAdminControllerDefinirAtivo,
  useEspecialidadesDiscussaoAdminControllerListar,
} from '@/api/generated/admin-especialidades-de-discussão/admin-especialidades-de-discussão';
import type { EspecialidadeDiscussaoResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { EspecialidadeFormDialogo } from '@/features/admin/especialidades-discussao/components/EspecialidadeFormDialogo';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

/**
 * Catálogo de especialidades de discussão — cadastro é aqui, FORA do wizard
 * de abertura. O médico só escolhe entre as já cadastradas, pelo picker do
 * passo 2 (`discussoes.visualizar`); cadastrar e remover são ações de admin
 * (`admin_especialidades_discussao.criar` / `.editar`), separadas de propósito.
 */
export function EspecialidadesDiscussaoPage() {
  const queryClient = useQueryClient();
  const { pode } = usePermissoes();
  const podeCriar = pode('admin_especialidades_discussao.criar');
  const podeEditar = pode('admin_especialidades_discussao.editar');

  const [formAberto, setFormAberto] = useState(false);
  const [removendo, setRemovendo] = useState<EspecialidadeDiscussaoResponse | null>(null);

  const { data, isPending, isError, refetch } = useEspecialidadesDiscussaoAdminControllerListar();
  const definirAtivo = useEspecialidadesDiscussaoAdminControllerDefinirAtivo();

  async function aoConfirmarRemocao() {
    if (!removendo) return;
    try {
      await definirAtivo.mutateAsync({ id: removendo.id, data: { ativo: false } });
      await queryClient.invalidateQueries({
        queryKey: getEspecialidadesDiscussaoAdminControllerListarQueryKey(),
      });
      toast.success(`"${removendo.nome}" removida.`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível remover a especialidade.');
    } finally {
      setRemovendo(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        {podeCriar && (
          <Button onClick={() => setFormAberto(true)}>
            <Plus className="w-4 h-4" />
            Nova especialidade
          </Button>
        )}
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">
            Especialidades cadastradas
          </h3>
        </div>

        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={Sparkles}
            titulo="Nenhuma especialidade cadastrada"
            descricao={
              podeCriar
                ? 'Cadastre a primeira acima.'
                : 'Nenhum admin cadastrou uma especialidade ainda.'
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead className="w-20">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.itens.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="text-foreground">{e.nome}</TableCell>
                    <TableCell>
                      {podeEditar && (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Remover ${e.nome}`}
                          onClick={() => setRemovendo(e)}
                        >
                          <PowerOff className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <EspecialidadeFormDialogo aberto={formAberto} aoFechar={() => setFormAberto(false)} />

      <AlertDialog open={Boolean(removendo)} onOpenChange={(v) => !v && setRemovendo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover &quot;{removendo?.nome}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>
              Some do picker de abertura de discussão. Discussões que já usam essa especialidade
              não são afetadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void aoConfirmarRemocao()}>Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
